/* eslint-disable react-hooks/exhaustive-deps */
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';
import React, { useEffect, useState, useRef } from 'react';
import "./fonts/style.scss";
import './App.scss';
import Header from './components/header/header';
import ImsWebHelper from "./ims/imswebhelper";
import { ImsData } from "./ims/ims-data";
import Help from './components/help/help';
import Home from './components/home/home';
import Cloud from './components/cloud/cloud';
import Events from './components/events/events';
import EventSubscription from './components/eventSubscription/eventSubscription';
import Footer from './components/footer/footer';
import enlocales from "./locales/en.json";
import {
  BrowserRouter as Router,
  Switch,
  Route
} from "react-router-dom";
import { LocaleService } from './models/locale-service';
import ManageSubscription from './components/managesubscription/managesubscription';
import PnsUnsubscribeAll from './components/pnsunsubscribeall/pnsunsubscribeall';
import UnSubscribeProduct from './components/unsubscribeproduct/unsubscribeproduct';
import ProactiveNotification from './components/proactivenotification/proactivenotification';
import SlackLanding from './components/slack/slacklanding';
import SlackError from './components/slack/error';
import SlackSuccess from './components/slack/success';
import TeamsLanding from './components/teams/teamslanding';
import { ImsWebConstants, IMS_EVENT, OMEGA_ENV, SHOW_CHATBOT, URLS} from './constants';
import { UserAccessService } from './models/user-access-service';
import HistoryRedirection from './components/historyRediretcion/historyRediretcion';
import OMEGA from './omega';

import Chatbot from 'react-chatbot-kit';
import ActionProvider from "./components/home/chatbot/ActionProvider";
import MessageParser from "./components/home/chatbot/MessageParser";
import HelpEvents from './components/helpEvents/helpEvents';
import CFSNew from './components/cfsnew/cfsnew';
import { ButtonRegistryProvider } from "./ButtonRegistryContext";
import TeamsSetup from './components/teamssetup/teamssetup';
import TeamsError from './components/teams/error';
import SetupChannels from './components/setupchannels/setupchannels';

// Remove the global interface declaration that's causing errors
export const ConfigContext = React.createContext<any>({});

// Move ChatbotWrapper outside of AppContent to prevent redefinition
interface ChatbotWrapperProps {
  userHasAccess: boolean | null;
  setUserHasAccess: (access: boolean | null) => void;
}

const ChatbotWrapper = ({ userHasAccess, setUserHasAccess }: ChatbotWrapperProps) => {
  const [error, setError] = useState(false);
  const [showFirstTimeDisclaimer, setShowFirstTimeDisclaimer] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [hasSeenDisclaimer, setHasSeenDisclaimer] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);
  const [isSubmittingDisclaimer, setIsSubmittingDisclaimer] = useState(false);
  const [disclaimerError, setDisclaimerError] = useState('');
  
  // Ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper function to track errors to Omega
  const trackErrorToOmega = (errorMessage: string, error: any) => {
    OMEGA.init().updateAndTrack("event", [{
      element: `${errorMessage}: ${error?.message || error?.toString() || 'Unknown error'}`,
      action: "error",
      type: "Component",
      widget: {
        name: "App",
        type: "error"
      }
    }]);
  };

  // Helper function to get user info
  const getUserInfo = () => {
    const imsProfile = sessionStorage.getItem('adobeid_ims_profile');
    if (imsProfile) {
      const profile = JSON.parse(imsProfile);
      return profile.email || '';
    }
    return '';
  };

  // Function to check disclaimer status via API
  const checkDisclaimerStatus = async (userEmail: string) => {
    try {
      const response = await fetch(`${URLS.DISCLAIMER_URL}/${userEmail}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${window.adobeIMS?.getAccessToken()?.token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        return data.first_time_disclaimer === true;
      }
      return false;
    } catch (error: any) {
      console.error('Error checking disclaimer status:', error);
      trackErrorToOmega('Error checking disclaimer status', error);
      return false;
    }
  };

  // Function to check user access for chat input
  const checkUserAccess = async () => {
    const userEmail = getUserInfo();
    if (!userEmail) {
      setUserHasAccess(false);
      return;
    }

    try {
      const hasAccess = await UserAccessService.checkUserAccess(userEmail);
      if (isMountedRef.current) {
        setUserHasAccess(hasAccess);
      }
    } catch (error: any) {
      console.error('Error checking user access:', error);
      trackErrorToOmega('Error checking user access', error);
      if (isMountedRef.current) {
        setUserHasAccess(false);
      }
    }
  };

  // Function to set disclaimer status via API
  const setDisclaimerStatus = async (userEmail: string) => {
    try {
      const response = await fetch(URLS.DISCLAIMER_URL, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${window.adobeIMS?.getAccessToken()?.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userEmail,
          first_time_disclaimer: true,
        }),
      });

      if (response.ok) {
        console.log('Disclaimer status updated successfully');
        return true;
      } else {
        console.error('Failed to update disclaimer status');
        return false;
      }
    } catch (error: any) {
      console.error('Error setting disclaimer status:', error);
      trackErrorToOmega('Error setting disclaimer status', error);
      return false;
    }
  };

  // Effect to check user access when component mounts
  useEffect(() => {
    checkUserAccess();
  }, []);

  // Effect to check disclaimer status when component mounts
  useEffect(() => {
    const checkUserDisclaimerStatus = async () => {
      const userEmail = getUserInfo();
      if (userEmail && window.adobeIMS?.isSignedInUser() && isMountedRef.current) {
        const disclaimerStatus = await checkDisclaimerStatus(userEmail);
        if (isMountedRef.current) {
          setHasSeenDisclaimer(disclaimerStatus);
          setDisclaimerChecked(true);
        }
      }
    };

    if (!disclaimerChecked) {
      checkUserDisclaimerStatus();
    }
  }, [disclaimerChecked]);

  // Simple function to add AI assistant label
  const addAIAssistantLabel = () => {
    const inputContainer = document.querySelector('.react-chatbot-kit-chat-input-container');
    if (inputContainer && !inputContainer.querySelector('.ai-assistant-label')) {
      const label = document.createElement('div');
      label.className = 'ai-assistant-label';
      label.textContent = 'AI Assistant';
      inputContainer.insertBefore(label, inputContainer.firstChild);
    }
  };

    // Effect to setup input and add event listeners
  useEffect(() => {    
    const setupInputContainer = () => {
      const inputContainer = document.querySelector('.react-chatbot-kit-chat-input-container');
      const input = document.querySelector('.react-chatbot-kit-chat-input') as HTMLInputElement;
      const sendButton = document.querySelector('.react-chatbot-kit-chat-btn-send') as HTMLButtonElement;
      
      if (inputContainer && input && sendButton) {
        inputContainer.classList.remove('conversation-loading');
        
        // Add AI assistant label
        addAIAssistantLabel();
        
        // Handle input container visibility based on user access
        if (userHasAccess === true) {
          // User has access - show and enable input
          inputContainer.classList.remove('access-denied');
          input.disabled = false;
          input.placeholder = 'Ask your question...';
          sendButton.disabled = false;
          sendButton.setAttribute('aria-label', 'Send message');
        } else {
          // Hide for unauthorized users or while checking access
          inputContainer.classList.add('access-denied');
        }
      }
    };

    // Setup input container
    setupInputContainer();

    // Add click listener to chat input to detect first interaction
    const handleInputClick = () => {
      if (!hasSeenDisclaimer && !hasUserInteracted && disclaimerChecked && userHasAccess) {
        setShowFirstTimeDisclaimer(true);
        setHasUserInteracted(true);
      }
    };

    const input = document.querySelector('.react-chatbot-kit-chat-input') as HTMLInputElement;
    if (input && disclaimerChecked && userHasAccess) {
      input.addEventListener('click', handleInputClick);
      input.addEventListener('focus', handleInputClick);
    }

    return () => {
      if (input) {
        input.removeEventListener('click', handleInputClick);
        input.removeEventListener('focus', handleInputClick);
      }
    };
  }, [hasSeenDisclaimer, hasUserInteracted, disclaimerChecked, userHasAccess]);

  const handleGetStarted = async () => {
    const userEmail = getUserInfo();
    if (userEmail) {
      setIsSubmittingDisclaimer(true);
      setDisclaimerError(''); // Clear any previous errors
      try {
        const success = await setDisclaimerStatus(userEmail);
        if (isMountedRef.current) {
          if (success) {
            setHasSeenDisclaimer(true); // Update local state
            setShowFirstTimeDisclaimer(false);
            
            // Focus the input after closing the disclaimer
            setTimeout(() => {
              const input = document.querySelector('.react-chatbot-kit-chat-input') as HTMLInputElement;
              if (input) {
                input.focus();
              }
            }, 100);
          } else {
            setDisclaimerError('We are currently facing issues, please try again later');
          }
        }
      } catch (error: any) {
        console.error('Error in handleGetStarted:', error);
        trackErrorToOmega('Error submitting disclaimer acceptance', error);
        if (isMountedRef.current) {
          setDisclaimerError('We are currently facing issues, please try again later');
        }
      } finally {
        if (isMountedRef.current) {
          setIsSubmittingDisclaimer(false);
        }
      }
    }
  };

  return (
    <>
      {error ? <div>Sorry, we are unable to process your request. Please try again later.</div> :
      <div className="chatbot-wrapper">
        <Chatbot
          key={`chatbot-${userHasAccess}`}
          config={require('./components/home/chatbot/config.js').default(userHasAccess)}
          actionProvider={ActionProvider}
          messageParser={MessageParser}
          headerText='Adobe Virtual Assistant'
        />
        {/* Only show disclaimer if user has access to text input */}
        {userHasAccess && (
          <div className="chatbot-disclaimer">
            AI-generated responses may be inaccurate or misleading. Be sure to double-check the responses and sources. <a href="https://www.adobe.com/legal/licenses-terms/adobe-gen-ai-user-guidelines.html" target="_blank" rel="noopener noreferrer" className="chatbot-disclaimer-link">User Guidelines.</a>
          </div>
        )}



        {/* First Time Disclaimer Overlay */}
        {showFirstTimeDisclaimer && userHasAccess && (
          <div className="chatbot-first-time-overlay">
            <div className="chatbot-first-time-content">
              <p className="chatbot-first-time-text">
                AI-generated responses may be inaccurate or misleading. Be sure to double-check the responses and sources.
              </p>
              <p className="chatbot-first-time-text">
                Ready to try? By clicking Get Started, you agree to our <a href="https://www.adobe.com/legal/licenses-terms/adobe-gen-ai-user-guidelines.html" target="_blank" rel="noopener noreferrer" className="chatbot-disclaimer-link">User Guidelines</a>.
              </p>
              
              {disclaimerError && (
                <div className="chatbot-error-message">
                  {disclaimerError}
                </div>
              )}
              
              <button 
                className={`chatbot-get-started-btn ${isSubmittingDisclaimer ? 'loading' : ''}`}
                onClick={handleGetStarted}
                disabled={isSubmittingDisclaimer}
                aria-label={isSubmittingDisclaimer ? "Saving preferences..." : "Get started with Adobe Status Virtual Assistant"}
              >
                {isSubmittingDisclaimer ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  'Get Started'
                )}
              </button>
            </div>
          </div>
        )}
      </div>}
    </>
  );
};

// Create AppContent component to use hooks within ChatbotProvider
function AppContent() {
  const [appConfig, setAppConfig] = useState({
    languageData: enlocales,
    menu: 'Overview'
  });
  const IMSEvent = new Event(IMS_EVENT);

  const locales: any = LocaleService.init();

  const [loggedIn, setLoggedIn] = useState(false);
  const [showBot, setShowBot] = useState(false);
  const [initialChatMessage, setInitialChatMessage] = useState("");
  const [error, setError] = useState(false);
  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);

  // Each time the assistant is opened, drop the previous ASVA session id so
  // the backend issues a fresh one; it then stays unchanged for as long as
  // this chat stays open (session id is otherwise persisted in sessionStorage).
  useEffect(() => {
    if (showBot) {
      sessionStorage.removeItem('aiSessionId');
    }
  }, [showBot]);

  // Helper function to track errors to Omega
  const trackErrorToOmega = (errorMessage: string, error: any) => {
    OMEGA.init().updateAndTrack("event", [{
      element: `${errorMessage}: ${error?.message || error?.toString() || 'Unknown error'}`,
      action: "error",
      type: "Component",
      widget: {
        name: "App",
        type: "error"
      }
    }]);
  };

  function updateMenu(menu: any) {
    setAppConfig({
      languageData: locales?.localeData || enlocales,
      menu: menu
    });
  }
  
  const context: any = {
    updateMenu,
    appConfig,
    showBot,
    setShowBot
  };
  const onStateChanged = (state: any) => {
    if (state.isSignedInUser) {
      window.adobeIMS?.validateToken();
      window.adobeIMS?.getProfile()
        .then((profile: any) => {
          sessionStorage.setItem('adobeid_ims_profile', JSON.stringify(profile));
          // Update digitalData immediately after profile is stored
          OMEGA.init().updateDigitalDataUserInfo();
          if (state?.ready) {
            document.dispatchEvent(IMSEvent);
          }
        })
        .catch((error: any) => {
          console.warn('Failed to get user profile after sign-in:', error);
          trackErrorToOmega('Error getting user profile after sign-in', error);
          if (state?.ready) {
            document.dispatchEvent(IMSEvent);
          }
        });
    }
    if (state?.ready) {
      if (state.isSignedInUser) {
        window.adobeIMS?.validateToken().catch((error: any) => {
          console.error('Token validation failed:', error);
          trackErrorToOmega('Error validating token', error);
          // Only strip the hash when it's IMS's own leftover bookkeeping fragment
          // (e.g. "#old_hash=...&from_ims=true") - reusing that verbatim can corrupt the
          // next authorize URL the IMS lib builds. Leave any other hash (e.g. "#adminconsole",
          // "#myevents", "#event") intact so those deep links survive a forced re-auth.
          const currentHref = window.location.href;
          window.adobeid.redirect_uri = currentHref.includes('from_ims=true')
            ? currentHref.split('#')[0]
            : currentHref;
          window.adobeIMS?.signIn();
        });
      } else {
        document.dispatchEvent(IMSEvent);
      }
    }
  };

  useEffect(() => {
    var currentLocaleData = locales.fetch();
    const imsData = new ImsData(onStateChanged);
    window.adobeid = imsData.adobeIdData;
   
    (window as any).setShowBotState = setShowBot;
    (window as any).setInitialChatMessage = setInitialChatMessage;

    
    if (window?.adobeIMS) {
      getIMS();
    } else {
      document.addEventListener(IMS_EVENT, getIMS);
    }
    const pageDetail = {
      solution: {
        name: "status",
        version: "2022.3"
      },
      env: OMEGA_ENV,
      autoTrack: false
    };
    window.digitalData = {
      user: {
        id: "anonymous",
        corpId: "anonymous",
        corpName: "anonymous",
        authSystem: "anonymous",
        accountType: "anonymous",
        language: window.locale || "en:us"
      },
      page: pageDetail
    };

function getIMS(){

    if(window.adobeIMS?.isSignedInUser()){
      setLoggedIn(true);
    }
}


    ImsWebHelper.injectAdobeIms().then(() => {
      ImsWebHelper.IMSLoaded();
      ImsWebHelper.addScript(ImsWebConstants.omegaLocation).then(() => {
        if (window._satellite) { // OMEGA tigger
          window._satellite.pageBottom();
        }
      });

    });

    locales.setLocales(currentLocaleData);
    setAppConfig({
      languageData: currentLocaleData,
      menu: 'Overview',
    });

  }, []);

  // Listen for initialChatMessage changes to set the chatbot's initial message
  useEffect(() => {
    if (initialChatMessage && showBot) {
      try {
        // Call openChatWithWorkflow without arguments if it's causing an error
        // openChatWithWorkflow("findEvents");
      } catch (err) {
        console.error("Error opening chat with workflow:", err);
      }
      // Reset after processing
      setTimeout(() => {
        setInitialChatMessage("");
      }, 500);
    }
  }, [initialChatMessage, showBot]);


  return (
    <div className="App">
      
      <Router>
        <ConfigContext.Provider value={context}>
          <Header />
          <Switch>
            {/* <Route path="/cloud/:cloudName">
              <Cloud />
            </Route> */}
            <Route path="/cloud/:cloudName/:eventId?">
              <Cloud />
            </Route>
            {/* <Route path="/:locale/cloud/:cloudName">
              <Cloud />
            </Route> */}
            <Route path="/:locale/cloud/:cloudName/:eventId?">
              <Cloud />
            </Route>
            {/* <Route path="/products/:productId/:eventId?">
              <Events />
            </Route> */}

             <Route
                    path="/products/:productId/:eventId?"
                    render={({ match }) => (
                        <Events key={`${match.params.productId}-${match.params.eventId}`} /> // Dynamically set key
                    )}
                />
            <Route path="/:locale/products/:productId/:eventId?">
              <Events />
            </Route>
            <Route path="/history/:messageId">
              <HistoryRedirection />
            </Route>
            <Route path="/subscribe/:eventId/cloud/:cloudId?">
              <EventSubscription type="subscription" />
            </Route>
            <Route path="/:locale/subscribe/:eventId/cloud/:cloudId?">
              <EventSubscription type="subscription" />
            </Route>
            <Route path="/unsubscribe/:eventId/cloud/:cloudId?">
              <EventSubscription type="unsubscription" />
            </Route>
            <Route path="/:locale/unsubscribe/:eventId/cloud/:cloudId?">
              <EventSubscription type="unsubscription" />
            </Route>
            <Route path="/subscribe/:eventId/:productId?">
              <EventSubscription type="subscription" />
            </Route>
            <Route path="/:locale/subscribe/:eventId/:productId?">
              <EventSubscription type="subscription" />
            </Route>
            <Route path="/unsubscribe/:eventId/:productId?">
              <EventSubscription type="unsubscription" />
            </Route>
            <Route path="/:locale/unsubscribe/:eventId/:productId?">
              <EventSubscription type="unsubscription" />
            </Route>
            <Route path="/proactive-notifications/manage">
              <ManageSubscription />
            </Route>
            <Route path="/:locale/proactive-notifications/manage">
              <ManageSubscription />
            </Route>
            <Route path="/proactive-notifications/subscriptions/:type">
              <ProactiveNotification />
            </Route>
            <Route path="/:locale/proactive-notifications/product/unsubscribe-all">
              <PnsUnsubscribeAll />
            </Route>
            <Route path="/proactive-notifications/product/unsubscribe-all">
              <PnsUnsubscribeAll />
            </Route>
            <Route path="/:locale/proactive-notifications/product/unsubscribe-all">
              <PnsUnsubscribeAll />
            </Route>
            <Route path="/proactive-notifications/product/unsubscribe/:productId">
              <UnSubscribeProduct />
            </Route>
            <Route path="/:locale/proactive-notifications/product/unsubscribe/:productId">
              <UnSubscribeProduct />
            </Route>
            <Route path="/teams-setup">
              <TeamsSetup />
            </Route>
            <Route path="/teams/error">
              <TeamsError />
            </Route>
            <Route path="/:locale/teams/error">
              <TeamsError />
            </Route>
            <Route path="/:locale/teams-setup">
              <TeamsSetup />
            </Route>
            <Route path="/setup-channels">
              <SetupChannels />
            </Route>
            <Route path="/:locale/setup-channels">
              <SetupChannels />
            </Route>
            <Route path="/slack/landing">
              <SlackLanding />
            </Route>
            <Route path="/:locale/slack/landing">
              <SlackLanding />
            </Route>
            <Route path="/slack/error">
              <SlackError />
            </Route>
            <Route path="/:locale/slack/error">
              <SlackError />
            </Route>
            <Route path="/slack/success">
              <SlackSuccess />
            </Route>
            <Route path="/:locale/slack/success">
              <SlackSuccess />
            </Route>
            <Route path="/teams/landing">
              <TeamsLanding />
            </Route>
            <Route path="/:locale/teams/landing">
              <TeamsLanding />
            </Route>
            <Route path="/help/proactivesubscriptions" >
              <Help />
            </Route>
            <Route path="/:locale/help/proactivesubscriptions" >
              <Help />
            </Route>
            <Route path="/help/eventglossary" >
              <HelpEvents />
            </Route>
            <Route path="/:locale/help/eventglossary" >
              <HelpEvents />
            </Route>
            {/* <Route path="/help/cfs" >
              <CFSEvents />
            </Route>
            <Route path="/:locale/help/cfs" >
              <CFSEvents />
            </Route> */}
            {/* <Route path="/help/cfs" >
              <CFSNew />
            </Route>
            <Route path="/:locale/help/cfs" >
              <CFSNew />
            </Route> */}


            <Route path="/nav" >
              <Home />
            </Route>
            <Route path="/:locale/nav" >
            </Route>
            <Route path="/:locale/:eventId?" >
              <Home />
            </Route>      
            <Route path="/" exact>
              <Home />
            </Route>
          </Switch>
          
          {/* Chatbot moved after content to appear later in tab order */}
          <div className="chatbot-div-wrapper">
              { 
                (JSON.parse(SHOW_CHATBOT) && loggedIn) && (
                                      <>
                      {showBot ? (
                        <div className={`chatbot-div ${userHasAccess === false ? 'no-text-input' : ''}`}>
                          <div className="chatbot">
                            <ChatbotWrapper userHasAccess={userHasAccess} setUserHasAccess={setUserHasAccess} />
                          </div>
                        </div>
                    ) : null}
                    <button
                      className={showBot ? "chatbot-button active" : "chatbot-button"}
                      style={{
                        width: 'min(60px, calc(100vw - 20px))',
                        height: 'min(60px, calc(100vh - 20px))',
                        minWidth: '44px',
                        minHeight: '44px'
                      }}
                      onClick={async () => {
                        const wasOpen = showBot;
                                              
                        setShowBot(!showBot);
                        
                        // When opening the chatbot, focus the message container
                        if (!wasOpen) {
                          // Give it a longer timeout to ensure the chatbot is fully rendered
                          setTimeout(() => {
                            // Specifically target the message container as requested
                            const messageContainer = document.querySelector('.react-chatbot-kit-chat-message-container');
                            if (messageContainer) {
                              // Make it programmatically focusable
                              (messageContainer as HTMLElement).tabIndex = -1;
                              (messageContainer as HTMLElement).focus();
                              return;
                            }
                          }, 300); // Timeout to ensure rendering is complete
                        }
                      }}
                      aria-label={showBot ? "Ask our assistant - close virtual assistant" : "Ask our assistant - open virtual assistant"}
                      aria-expanded={showBot}
                    >
                      <span className="visually-hidden">{showBot ? "Close" : "Open"} virtual assistant</span>
                      <div className="tooltip">Ask our assistant</div>
                    </button>
                  </>
                )
              }
          </div>
          
          <Footer />
        </ConfigContext.Provider>
      </Router>
    </div>
  );
}

// Main App component that provides the ChatbotProvider
function App() {
  return (
    // <ChatbotProvider>
      <ButtonRegistryProvider>
        <AppContent />
      </ButtonRegistryProvider>
    // </ChatbotProvider>
  );
}

export default App;
